import { isDefined } from "@clipboard-health/util-ts";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { z } from "zod";

import { type PushPermissionState } from "./usePushNotificationRegistration";

const PROMPT_STATE_STORAGE_KEY_PREFIX = "pushNotificationPromptState_";
const HAS_VIEWED_CALENDAR_KEY_PREFIX = "hasViewedCalendar_";

function getPromptStateKey(userId: string): string {
  return `${PROMPT_STATE_STORAGE_KEY_PREFIX}${userId}`;
}

function getCalendarViewedKey(userId: string): string {
  return `${HAS_VIEWED_CALENDAR_KEY_PREFIX}${userId}`;
}
const DEFERRAL_COOLDOWN_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

export const PUSH_PROMPT_STATES = {
  not_prompted: "not_prompted",
  deferred: "deferred",
  granted: "granted",
  denied: "denied",
} as const;

export type PushPromptState = (typeof PUSH_PROMPT_STATES)[keyof typeof PUSH_PROMPT_STATES];

const storedPromptStateSchema = z.object({
  state: z.enum(["not_prompted", "deferred", "granted", "denied"]),
  dismissedAt: z.number().optional(),
});

interface StoredPromptState {
  state: PushPromptState;
  dismissedAt?: number;
}

function getStoredPromptState(userId: string): StoredPromptState {
  try {
    const stored = localStorage.getItem(getPromptStateKey(userId));
    if (!stored) {
      return { state: "not_prompted" };
    }

    return storedPromptStateSchema.parse(JSON.parse(stored));
  } catch {
    return { state: "not_prompted" };
  }
}

function setStoredPromptState(userId: string, state: StoredPromptState): void {
  try {
    localStorage.setItem(getPromptStateKey(userId), JSON.stringify(state));
  } catch {
    // localStorage may be unavailable
  }
}

// Reactive external store for hasViewedCalendar so useSyncExternalStore
// picks up changes made by markCalendarViewed() within the same session.
const calendarViewedListeners = new Set<() => void>();

function subscribeToCalendarViewed(callback: () => void): () => void {
  calendarViewedListeners.add(callback);
  return () => {
    calendarViewedListeners.delete(callback);
  };
}

function getCalendarViewedSnapshot(userId: string): boolean {
  try {
    return localStorage.getItem(getCalendarViewedKey(userId)) === "true";
  } catch {
    return false;
  }
}

/**
 * Records that the user has viewed the calendar.
 * Called from the calendar page to enable the push notification soft prompt trigger.
 */
export function markCalendarViewed(userId: string): void {
  try {
    localStorage.setItem(getCalendarViewedKey(userId), "true");
  } catch {
    // localStorage may be unavailable
  }

  for (const listener of calendarViewedListeners) {
    listener();
  }
}

interface UsePushNotificationPromptStateParams {
  permissionState: PushPermissionState;
  userId: string;
}

interface UsePushNotificationPromptStateResult {
  shouldShowPrompt: boolean;
  promptState: PushPromptState;
  onDismiss: () => void;
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
}

export function usePushNotificationPromptState(
  params: UsePushNotificationPromptStateParams
): UsePushNotificationPromptStateResult {
  const { permissionState, userId } = params;

  const getSnapshot = useCallback(() => getCalendarViewedSnapshot(userId), [userId]);
  const calendarViewed = useSyncExternalStore(subscribeToCalendarViewed, getSnapshot);

  const [storedState, setStoredState] = useState(() => getStoredPromptState(userId));

  useEffect(() => {
    setStoredState(getStoredPromptState(userId));
  }, [userId]);

  const promptState = useMemo((): PushPromptState => {
    // Sync with actual permission state
    if (permissionState === "granted") {
      return "granted";
    }
    if (permissionState === "denied") {
      return "denied";
    }

    return storedState.state;
  }, [permissionState, storedState.state]);

  const shouldShowPrompt = useMemo((): boolean => {
    // Never show if permission is already resolved
    if (permissionState !== "prompt") {
      return false;
    }

    // Don't show until the user has viewed the calendar at least once
    if (!calendarViewed) {
      return false;
    }

    // Never show again after grant or permanent denial
    if (storedState.state === "granted" || storedState.state === "denied") {
      return false;
    }

    // If deferred, check cooldown
    if (storedState.state === "deferred" && isDefined(storedState.dismissedAt)) {
      const elapsed = Date.now() - storedState.dismissedAt;
      return elapsed >= DEFERRAL_COOLDOWN_MS;
    }

    // "not_prompted" — show it
    return true;
  }, [calendarViewed, permissionState, storedState]);

  const onDismiss = useCallback(() => {
    const newState: StoredPromptState = { state: "deferred", dismissedAt: Date.now() };
    setStoredPromptState(userId, newState);
    setStoredState(newState);
  }, [userId]);

  const onPermissionGranted = useCallback(() => {
    const newState: StoredPromptState = { state: "granted" };
    setStoredPromptState(userId, newState);
    setStoredState(newState);
  }, [userId]);

  const onPermissionDenied = useCallback(() => {
    const newState: StoredPromptState = { state: "denied" };
    setStoredPromptState(userId, newState);
    setStoredState(newState);
  }, [userId]);

  return {
    shouldShowPrompt,
    promptState,
    onDismiss,
    onPermissionGranted,
    onPermissionDenied,
  };
}
